"use client";

import { useState } from "react";
import { Box, Stack, Text } from "@chakra-ui/react";

interface InsightSection {
  label: string;
  content: string;
}

interface AiInsightContentProps {
  loading: boolean;
  error: string | null;
  status: "ok" | "insufficient_data" | null;
  preview: InsightSection | null;
  sections: InsightSection[];
  expandHint: string;
  emptyMessage: string;
}

export function AiInsightContent({
  loading,
  error,
  status,
  preview,
  sections,
  expandHint,
  emptyMessage,
}: AiInsightContentProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Box w="100%" h="44px" borderRadius="6px" className="skeleton" />
    );
  }

  if (error) {
    return (
      <Text fontSize="13px" color="var(--color-text-secondary)" lineHeight="1.6">
        Unable to generate insights right now. {error}
      </Text>
    );
  }

  if (status !== "ok" || !preview) {
    return (
      <Text fontSize="13px" color="var(--color-text-secondary)" lineHeight="1.6">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <Stack gap={3}>
      <Text fontSize="13px" color="var(--color-text-secondary)" lineHeight="1.6">
        <Text as="span" fontWeight="600" color="var(--color-text-primary)">
          {preview.label}:
        </Text>{" "}
        {preview.content}
      </Text>

      {sections.length > 0 && (
        <Box>
          <Box
            as="button"
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            fontSize="12px"
            fontWeight="600"
            color="rgba(99, 102, 241, 0.9)"
            border="0.5px solid rgba(99, 102, 241, 0.3)"
            borderRadius="6px"
            px={2.5}
            py={1}
            bg="rgba(99, 102, 241, 0.04)"
            transition="all 150ms cubic-bezier(0.25, 1, 0.5, 1)"
            _hover={{
              bg: "rgba(99, 102, 241, 0.08)",
              borderColor: "rgba(99, 102, 241, 0.5)",
            }}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Hide detailed analysis" : "Read full analysis"}
          </Box>
          {!expanded && (
            <Text fontSize="11px" color="var(--color-text-tertiary)" mt={1.5}>
              {expandHint}
            </Text>
          )}
        </Box>
      )}

      {expanded && (
        <Stack gap={2}>
          {sections.map(({ label, content }) => (
            <Text
              key={label}
              fontSize="13px"
              color="var(--color-text-secondary)"
              lineHeight="1.6"
            >
              <Text as="span" fontWeight="600" color="var(--color-text-primary)">
                {label}:
              </Text>{" "}
              {content}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
